import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { Observable, Subject, takeUntil } from 'rxjs';

export interface ApiAutocompleteOption {
    label: string;
    value: any;
    keywords?: string[];
}

@Component({
    selector: 'ashcorp-api-autocomplete',
    templateUrl: './api-autocomplete.component.html',
    styleUrls: ['./api-autocomplete.component.css'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class AshcorpApiAutocompleteComponent implements OnInit, OnDestroy, OnChanges {
    private _unsubscribeAll: Subject<void> = new Subject<void>();

    constructor(private _cdr: ChangeDetectorRef) { }

    @Input() form!: FormGroup;
    @Input() label = '';
    @Input() name = '';
    @Input() required = false;
    @Input() placeholder = '';
    @Input() reloadTrigger: unknown;

    // Provide a function reference, for example: () => this._appsService.list('name', 'asc', 0, 500)
    @Input() fetchOptions!: () => Observable<any>;

    // Optional mapper for adapting service response to autocomplete options.
    @Input() mapOptions: ((response: any) => ApiAutocompleteOption[]) | null = null;

    options: ApiAutocompleteOption[] = [];
    filteredOptions: ApiAutocompleteOption[] = [];

    get id(): string {
        return `api-autocomplete-${this.name || this.label || 'field'}`.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    }

    get control(): AbstractControl | null {
        if (!this.form || !this.name) {
            return null;
        }

        return this.form.get(this.name);
    }

    get errorMessage(): string {
        const control = this.control;
        if (!control) {
            return '';
        }

        const serverError = control.getError('server');
        if (serverError) {
            return serverError;
        }

        if (control.hasError('required')) {
            return `${this.label || this.name} is required.`;
        }

        return '';
    }

    ngOnInit(): void {
        this._loadOptions();

        this.control?.valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((value) => {
                this._applyFilter(value);
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['reloadTrigger'] || changes['reloadTrigger'].firstChange) {
            return;
        }

        this._loadOptions();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    displayOption = (value: any): string => {
        if (!value) {
            return '';
        }

        const match = this.options.find((option) => option.value === value);
        if (match) {
            return match.label;
        }

        if (typeof value === 'string') {
            return value;
        }

        if (typeof value === 'object') {
            return value?.name || value?.label || value?.code || '';
        }

        return String(value);
    };

    private _loadOptions(): void {
        if (!this.fetchOptions) {
            this.options = [];
            this.filteredOptions = [];
            return;
        }

        this.fetchOptions()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    this.options = this.mapOptions
                        ? this.mapOptions(response)
                        : this._defaultMapOptions(response);
                    this._applyFilter(this.control?.value || '');
                },
                error: () => {
                    this.options = [];
                    this.filteredOptions = [];
                }
            });
    }

    private _applyFilter(value: any): void {
        const query = this._normalizeValue(value).toLowerCase();

        if (!query) {
            this.filteredOptions = [...this.options];
            this._cdr.markForCheck();
            return;
        }

        this.filteredOptions = this.options.filter((option) => {
            const label = (option.label || '').toLowerCase();
            const keywords = (option.keywords || []).join(' ').toLowerCase();
            return label.includes(query) || keywords.includes(query);
        });
        this._cdr.markForCheck();
    }

    private _normalizeValue(value: unknown): string {
        if (!value) {
            return '';
        }

        if (typeof value === 'string') {
            return value;
        }

        if (typeof value === 'object') {
            const asObject = value as { name?: string; label?: string; code?: string };
            return asObject.name || asObject.label || asObject.code || '';
        }

        return String(value);
    }

    private _defaultMapOptions(response: any): ApiAutocompleteOption[] {
        const source = Array.isArray(response)
            ? response
            : Array.isArray(response?.items)
                ? response.items
                : [];

        return source.map((item: any) => {
            if (typeof item === 'string' || typeof item === 'number') {
                return { label: String(item), value: item };
            }

            const label = item?.name ?? item?.label ?? item?.code ?? '';
            const value = item?.id ?? item?.code ?? label;

            return { label: String(label), value, keywords: [item?.name, item?.code].filter(Boolean) };
        });
    }
}
